import React, { useEffect, useState } from 'react';
import { Link, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import ReactDOM from 'react-dom';
import {
  PageContent,
  Spinner,
  CisContainer,
  Button,
  Row,
  ButtonIcon,
  Modal,
  Table,
} from 'components';
import { connect } from 'react-redux';
import {
  loadMaCisCreator,
  setMaCisPageCreator,
  setMaCisPerPageCreator,
  setMaCisSortCreator,
  setMaCisFiltersCreator,
  removeMaCisFiltersCreator,
  setMaCisProfileCreator,
} from 'store/actionsCreators';
import PropTypes from 'prop-types';
import { isLoading, globalPropTypes, checkPermissions } from 'helpers';
import { removeMaCisCreator } from '../../../../store/actionsCreators';
import TableHeaderCell from 'components/Table/TableHeaderCell';
import CisTable from 'components/Table/CisTable';
import CisPagination from 'components/Table/CisPagination';
import moment from 'moment';

const Signatures = ({
  loading,
  list,
  loadList,
  setPage,
  setPerPage,
  setSort,
  setFilters,
  setProfile,
  removeSignature,
  userPermissions,
}) => {
  const { filters, items, page, pages, perPage, sort, totalItems } = list;
  const { t } = useTranslation();
  const history = useHistory();
  const [portalReady, setPortalReady] = useState(false);
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  const manageCisPermission = checkPermissions({ roles: ['ma_manage_cis'] }, userPermissions || []);

  useEffect(() => {
    if (document.getElementById('ma-cis-buttons-portal')) {
      setPortalReady(true);
    }
  }, []);

  useEffect(() => {
    setPage(1);
    loadList();
  }, [perPage, sort]);

  const handleColumnFilterChange = (key, value) => {
    setFilters({ name: key, value });
    setPage(1);
    loadList();
  };

  const getSortDirection = (key) => {
    if (!sort) return null;
    const [sortKey, sortDir] = sort.split('.');
    return sortKey === key ? sortDir : null;
  };

  const handleColumnSort = (key, direction) => {
    if (direction) {
      setSort(`${key}.${direction}`);
    } else {
      setSort(null);
    }
  };

  return (
    <PageContent>
      <CisContainer>
        <CisTable>
          <CisTable.Head>
            <CisTable.Row>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('global.id')} />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="input"
                  filterValue={filters.fullName || ''}
                  onFilter={(val) => handleColumnFilterChange('fullName', val)}
                  filterPlaceholder={t('page.user.cis.signatures.fullNamePlaceholder')}
                  label={t('page.user.cis.signatures.fullName')}
                  sort
                  sortDirection={getSortDirection('fullName')}
                  onSort={(dir) => handleColumnSort('fullName', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  filter
                  filterType="datepicker-range"
                  filterValueFrom={filters.dateFromFrom || ''}
                  filterValueTo={filters.dateFromTo || ''}
                  onFilterFrom={(val) => handleColumnFilterChange('dateFromFrom', val)}
                  onFilterTo={(val) => handleColumnFilterChange('dateFromTo', val)}
                  filterPlaceholder={t('page.user.cis.signatures.dateFromPlaceholder')}
                  label={t('page.user.cis.signatures.dateFrom')}
                  labelAlign="center"
                  sort
                  sortDirection={getSortDirection('dateFrom')}
                  onSort={(dir) => handleColumnSort('dateFrom', dir)}
                />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell label={t('page.user.cis.signatures.type')} labelAlign="center" />
              </CisTable.StickyHeading>
              <CisTable.StickyHeading>
                <TableHeaderCell
                  label={t('page.user.cis.signatures.signature')}
                  labelAlign="center"
                />
              </CisTable.StickyHeading>
              {manageCisPermission && (
                <CisTable.StickyActionHeading style={{ minWidth: '10rem' }}>
                  <TableHeaderCell
                    label={t('page.user.cis.signatures.actions')}
                    labelAlign="center"
                    isAction={true}
                  />
                </CisTable.StickyActionHeading>
              )}
            </CisTable.Row>
          </CisTable.Head>
          <CisTable.Body>
            {loading ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>
                  <Spinner />
                </CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && !items.length ? (
              <CisTable.Row>
                <CisTable.Cell colSpan={11}>{t('global.noResultsFound')}</CisTable.Cell>
              </CisTable.Row>
            ) : null}
            {!loading && items.length
              ? items.map((item) => (
                  <CisTable.Row key={item.id}>
                    <CisTable.Cell>
                      <Table.Label>{t('global.id')}:</Table.Label>
                      {item.id}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.fullName')}:</Table.Label>
                      {[item.user.firstName, item.user.middleName, item.user.lastName].join(' ')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.dateFrom')}:</Table.Label>
                      {moment(item.dateFrom).format('L')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.type')}:</Table.Label>
                      {item.assessorType === 'H' ? t('page.user.cis.signatures.head') : t('page.user.cis.signatures.assessor')}
                    </CisTable.Cell>
                    <CisTable.Cell>
                      <Table.Label>{t('page.user.cis.signatures.signature')}:</Table.Label>
                      {item.signatureType === 'I' && (
                        <span>{t('page.user.cis.signatures.signatureTypeItalic')}</span>
                      )}
                      {item.signatureType === 'N' && (
                        <span>{t('page.user.cis.signatures.signatureTypeNone')}</span>
                      )}
                      {item.signatureType === 'H' &&
                        (item.hasFileUrl ? t('global.handwritten') : t('global.noPicture'))}
                    </CisTable.Cell>
                    {manageCisPermission && (
                      <CisTable.StickyActionCell style={{ minWidth: '9.6rem' }}>
                        <Table.Label>{t('page.user.cis.signatures.actions')}:</Table.Label>
                        <Row style={{ gap: '3px', justifyContent: 'center' }}>
                          <ButtonIcon
                            icon="edit"
                            secondary
                            as={Button}
                            onClick={() => {
                              setProfile(item);
                              history.push(`/ma/cis/signatures/edit`);
                            }}
                          />
                          <ButtonIcon
                            icon="trash"
                            onClick={() => {
                              setSelectedItem(item);
                              setOpenConfirmModal(true);
                            }}
                          />
                        </Row>
                      </CisTable.StickyActionCell>
                    )}
                  </CisTable.Row>
                ))
              : null}
          </CisTable.Body>
        </CisTable>
      </CisContainer>
      <CisPagination
        styled
        currentPage={page}
        totalPages={pages}
        totalItems={totalItems}
        perPage={perPage}
        onPageChange={(payload) => {
          setPage(payload);
          loadList();
        }}
        onPerPageChange={({ value }) => setPerPage(value)}
      />
      <Modal
        heading={t('page.user.cis.signatures.confirmDelete')}
        isOpen={openConfirmModal && selectedItem?.id}
        confirm={() => {
          removeSignature(selectedItem.id);
          setOpenConfirmModal(false);
        }}
        cancel={() => setOpenConfirmModal(false)}
        onRequestClose={() => setOpenConfirmModal(false)}
      />
      {portalReady &&
        manageCisPermission &&
        ReactDOM.createPortal(
          <Button style={{ width: '30%' }} secondary as={Link} to={`/ma/cis/signatures/create`}>
            {t('page.user.cis.signatures.new')}
          </Button>,
          document.getElementById('ma-cis-buttons-portal'),
        )}
    </PageContent>
  );
};

Signatures.propTypes = {
  loading: PropTypes.bool,
  list: globalPropTypes.ListPropsTypes.isRequired,
  loadList: PropTypes.func.isRequired,
  setPage: PropTypes.func.isRequired,
  setPerPage: PropTypes.func.isRequired,
  setSort: PropTypes.func.isRequired,
  setFilters: PropTypes.func.isRequired,
  setProfile: PropTypes.func.isRequired,
  removeSignature: PropTypes.func.isRequired,
};

Signatures.defaultProps = {
  loading: false,
};

const mapStateToProps = ({ loading, ma, globals, user }) => ({
  loading: isLoading(loading, 'LOAD_MA_CIS'),
  list: ma.cis.signatures,
  countries: globals.countries,
  memberAssociations: globals.availableMA,
  userPermissions: user.userPermissions,
});

const mapDispatchToProps = (dispatch) => ({
  loadList: () => dispatch(loadMaCisCreator('signatures')),
  setPage: (payload) => dispatch(setMaCisPageCreator('signatures', payload)),
  setPerPage: (payload) => dispatch(setMaCisPerPageCreator('signatures', payload)),
  setSort: (payload) => dispatch(setMaCisSortCreator('signatures', payload)),
  setFilters: (payload) => dispatch(setMaCisFiltersCreator('signatures', payload)),
  removeFilters: () => dispatch(removeMaCisFiltersCreator('signatures')),
  setProfile: (payload) => dispatch(setMaCisProfileCreator('signatures', payload)),
  removeSignature: (payload) => dispatch(removeMaCisCreator('signatures', payload)),
});

export default connect(mapStateToProps, mapDispatchToProps)(Signatures);
